import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Clock, Loader2, Hourglass } from "lucide-react";
import type { IScheduledJob } from "src/@types/campaign-comparison";

const STATE_CONFIG: Record<
  IScheduledJob["state"],
  { label: string; icon: typeof Clock; className: string }
> = {
  active: {
    label: "Sincronizando",
    icon: Loader2,
    className: "bg-blue-500 text-white hover:bg-blue-600",
  },
  waiting: {
    label: "Na fila",
    icon: Hourglass,
    className: "bg-indigo-100 text-indigo-700 border-indigo-300",
  },
  delayed: {
    label: "Agendado",
    icon: Clock,
    className: "bg-indigo-100 text-indigo-700 border-indigo-300",
  },
};

const QUEUE_LABEL: Record<IScheduledJob["queue"], string> = {
  propagation: "propagação",
  edit: "resync",
};

const formatDateTime = (value: string | null): string | null =>
  value
    ? new Date(value).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

interface ScheduledJobBadgeProps {
  job: IScheduledJob;
  /** Exibe o detalhe (fila + horário agendado) ao lado do badge. */
  withDetails?: boolean;
  className?: string;
}

export function ScheduledJobBadge({
  job,
  withDetails = false,
  className,
}: ScheduledJobBadgeProps) {
  const config = STATE_CONFIG[job.state];
  const Icon = config.icon;
  const when = formatDateTime(job.scheduledFor);

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <Badge
        variant="outline"
        className={cn("gap-1", config.className)}
        title={`Sincronização de ${QUEUE_LABEL[job.queue]}${when ? ` — agendada para ${when}` : ""}`}
      >
        <Icon
          className={cn("h-3 w-3", job.state === "active" && "animate-spin")}
        />
        {config.label}
      </Badge>
      {withDetails && (
        <span className="text-xs text-muted-foreground">
          {QUEUE_LABEL[job.queue]}
          {when ? ` • ${when}` : ""}
        </span>
      )}
    </span>
  );
}
